// dependencies
import axios from "axios";
import { List, Map } from "immutable";
import React from "react";
import { connect } from "react-redux";

// constants
import Config from "./Config";
import GroupSearching from "./components/GroupSearching";
import LetterLabels from "./components/LetterLabels";
// components
import Pagination from "./components/Pagination";
import SearchBox from "./components/SearchContainer";
import SearchResults from "./components/SearchResults";

import { ParseDepartments } from "../../actions/departments.actions";
// actions
import { ParseFields } from "../../actions/fields.actions";
import { ChangeQueryParameters } from "../../actions/navigation.actions";
import { MergeStateChange, StateChange } from "../../actions/page.actions";
import { AddGroup, AddUser, RemoveUser, MergeSearch as Search } from "./actions";

// utils
import Cookies from "../../utils/Cookies";

import PropTypes from "../../lib/prop-types";

// props
const propTypes = {
	showLetterToggle: PropTypes.bool,
	storeColumnWidthsInCookies: PropTypes.bool,
	width: PropTypes.number.isRequired,
	height: PropTypes.number.isRequired,
	showlimitSelected: PropTypes.bool,
	showGroupSelector: PropTypes.bool,
	showLetterPagination: PropTypes.bool,
	showDownloadDirectoryLink: PropTypes.bool,
	showChangeColumnLink: PropTypes.bool,
	updateURLQuery: PropTypes.bool,
	storeName: PropTypes.string,
	selectionObjectId: PropTypes.string,
	searchPlaceholder: PropTypes.string,
	addGroupButton: PropTypes.string,
	exclusionList: PropTypes.instanceOf(List),
	usersToExcludeSelectionId: PropTypes.string,
	filters: PropTypes.instanceOf(Map),
	changePage: PropTypes.func,
	mergeState: PropTypes.func,
	searchUsers: PropTypes.func,
	addUserToSelectionObject: PropTypes.func.isRequired,
	addGroupToSelection: PropTypes.func.isRequired,
	removeUserFromSelectionObject: PropTypes.func.isRequired,
	urlQuery: PropTypes.instanceOf(Map),
	columns: PropTypes.instanceOf(List),
	changeState: PropTypes.func.isRequired,
	limitSelected: PropTypes.bool.isRequired,
	extraComponents: PropTypes.arrayOf(PropTypes.element),
	allowAllEmployees: PropTypes.bool,
	excludeCurrentUser: PropTypes.bool,
};

class Directory extends React.Component {
	/*
        Creates the directory component.

        - width {Integer} total width of the table
        - height {Integer} total height of the table

        - storeName {String} the location where the directory component should
        hold data in the 'page' reducer.

        - showDownloadDirectoryLink {Boolean} should the
        download directory link be rendered

        - showChangeColumnLink {Boolean} should the change column link be
        rendered

        - showLetterPagination {Boolean} should the letter paginator be
        rendered

        - updateURLQuery {Boolean} should query changes update the url query
        string. This will allow the user to click the back button to load the
        previous query. When this is turned on, the component will update the
        query string which will trigger the search action to execute from the
        'componentWillReceiveProps' method.

        - selectionObjectId {String} if passed into the props, the directory
        will use a selectionObject ID to do user searching.

        - exclusionList {List} if passed into the props, the directory
        will use a exclusionList IDs to filter out users.

    */

	componentDidMount() {
		this.props.mergeState(
			[],
			Map({
				page: 1,
				searchInput: "",
				letter: "",
				letterSearching: Cookies.get("letterSearching", "first"),
			})
		);
		this.cancelTokenSource = axios.CancelToken.source();
		this.cancelToken = this.cancelTokenSource.token;
		this.updateQueryFromURL(this.props);
	}

	componentDidUpdate(prevProps) {
		const { updateURLQuery = false, urlQuery = Map(), columns = List() } = this.props;
		const { columns: prevColumns = List() } = prevProps;
		const { urlQuery: prevUrlQuery = Map() } = prevProps;

		// if we detect a change in the url query or the columns, we should trigger a new search
		// the column differences can be caused by race conditions where the columns are not fully loaded when the component is mounted
		if (
			(urlQuery.hashCode() !== prevUrlQuery.hashCode() && updateURLQuery) ||
			columns.hashCode() !== prevColumns.hashCode()
		) {
			this.cancelTokenSource.cancel("Request canceled by newer search");
			this.cancelTokenSource = axios.CancelToken.source();
			this.cancelToken = this.cancelTokenSource.token;
			this.updateQueryFromURL(this.props);
		}
	}

	updateQueryFromURL = (props) => {
		/*
            Executes a query based on the URL parameters.
        */

		const searchObject = {};
		const { urlQuery = Map() } = props;

		// get data from cookie to sort by first or last name
		const cookie = Cookies.get("letterSearching", "first");
		const filterByLast = cookie === "last";

		const page = urlQuery.get("page");
		const query = urlQuery.get("query");
		const letter = urlQuery.get("letter");
		const lastName = filterByLast;

		// update the search input
		if (query) {
			searchObject.query = encodeURIComponent(query);
			this.props.changeState("searchInput", decodeURIComponent(query).replace(/\+/g, " "));
		}

		if (page) {
			searchObject.page = parseInt(page, 10);
		}

		if (letter !== undefined) {
			searchObject.letter = letter;
		}

		searchObject.lastName = lastName;

		this.handleSearch(searchObject, false);
	};

	handleUpdateQuery = (nextQuery = {}) => {
		const { updateURLQuery = false } = this.props;

		if (updateURLQuery) {
			this.props.changePage(nextQuery);
		} else {
			this.handleSearch(nextQuery);
		}
	};

	handleSearch = (search = {}) => {
		/*
            All searching in this component routes through this method.
        */

		const {
			filters = Map(),
			columns = List(),
			limitSelected,
			selectionObjectId = null,
			exclusionList = List(),
			usersToExcludeSelectionId = null,
			excludeCurrentUser = false,
		} = this.props;

		const searchObject = search;
		const searchFields = columns.map((col) => col.get("_id")).toArray(); // these are used to search these fields

		if (!Object.prototype.hasOwnProperty.call(searchObject, "limitSelected")) {
			searchObject.limitSelected = limitSelected;
		}

		this.props.searchUsers(
			searchObject,
			filters,
			searchFields,
			selectionObjectId,
			exclusionList,
			usersToExcludeSelectionId,
			excludeCurrentUser,
			this.cancelToken
		);
	};

	addGroupToSelection = async () => {
		const { addGroupButton, exclusionList = List(), filters = Map() } = this.props;
		await this.props.addGroupToSelection(addGroupButton, exclusionList, filters);
	};

	handleCheckboxChange = async (id, nextValue) => {
		const { selectionObjectId, exclusionList = List() } = this.props;
		if (nextValue === true) {
			await this.props.addUserToSelectionObject(selectionObjectId, id, exclusionList);
		} else {
			await this.props.removeUserFromSelectionObject(selectionObjectId, id);
		}
	};

	render() {
		const {
			width,
			height,
			columns = List(),
			filters = Map(),
			storeName = "DIRECTORY_CONTAINER",
			exclusionList = List(),
			updateURLQuery = false,
			addGroupButton = null,
			extraComponents = [],
			showLetterToggle = true,
			searchPlaceholder = "search...",
			showlimitSelected = false,
			showGroupSelector = false,
			selectionObjectId = null,
			showLetterPagination = true,
			showDownloadDirectoryLink = false,
			showChangeColumnLink = false,
			storeColumnWidthsInCookies = false,
			allowAllEmployees = true,
		} = this.props;

		const searchHeight = 32;
		const searchToTableSpacing = 20;
		const paginationHeight = 30;
		const tableToPaginationSpacing = 10;
		const marginLeft = showLetterPagination ? Config.labelsWidth + Config.spacing : 0;
		const tableHeight = height - searchHeight - searchToTableSpacing - paginationHeight - tableToPaginationSpacing;
		const tableWidth = width - marginLeft - 2; // the table width is 2px wider than what i set it to. probably due to border size.

		return (
			<div className="cp-directory">
				<section style={{ marginLeft }} className="body-container">
					<SearchBox
						storeName={storeName}
						placeholder={searchPlaceholder}
						onSearch={this.handleUpdateQuery}
						selectionObjectId={selectionObjectId}
						showlimitSelected={showlimitSelected}
						showGroupSelector={showGroupSelector}
						showAddGroupButton={!!addGroupButton}
						showChangeColumnLink={showChangeColumnLink}
						addGroupToSelection={this.addGroupToSelection}
						showDownloadDirectoryLink={showDownloadDirectoryLink}
					/>

					<SearchResults
						columns={columns}
						width={tableWidth}
						height={tableHeight}
						storeName={storeName}
						selectionObjectId={selectionObjectId}
						onCheckboxChange={this.handleCheckboxChange}
						storeColumnWidthsInCookies={storeColumnWidthsInCookies}
					/>

					<Pagination
						storeName={storeName}
						onSearch={this.handleUpdateQuery}
						selectionObjectId={selectionObjectId}
					/>
				</section>

				{showLetterPagination ? (
					<LetterLabels
						storeLabelSearchingInCookies={storeColumnWidthsInCookies}
						showLetterToggle={showLetterToggle}
						updateURLQuery={updateURLQuery}
						onSearch={this.handleUpdateQuery}
						height={height}
						storeName={storeName}
					/>
				) : (
					""
				)}
				<GroupSearching
					storeName={storeName}
					selectionObjectId={selectionObjectId}
					exclusionList={exclusionList}
					filters={filters}
					allowAllEmployees={allowAllEmployees}
				/>

				{extraComponents}
			</div>
		);
	}
}

Directory.propTypes = propTypes;

function mapStateToProps(state, ownProps) {
	return {
		nav: state.navigation,
		urlQuery: state.navigation.getIn(["page", "query"]),
		limitSelected: state.page.getIn([ownProps.storeName, "limitSelected"], false),
	};
}

function mapDispatchToProps(dispatch, ownProps) {
	return {
		parseFields: (fields) => dispatch(ParseFields(fields)),
		changePage: (params) => dispatch(ChangeQueryParameters(params)),
		parseDepartments: (departments) => dispatch(ParseDepartments(departments)),
		changeState: (keyPath, value) => dispatch(StateChange(ownProps.storeName, keyPath, value)),
		mergeState: (keyPath, value) => dispatch(MergeStateChange(ownProps.storeName, keyPath, value)),
		removeUserFromSelectionObject: (selectionObjectId, userId) =>
			dispatch(RemoveUser(ownProps.selectionObjectId, [userId], ownProps.storeName)),
		addUserToSelectionObject: (selectionObjectId, userId, exclusionList) =>
			dispatch(AddUser(ownProps.selectionObjectId, [userId], exclusionList, ownProps.storeName)),
		addGroupToSelection: (groupId, exclusionList, filters) =>
			dispatch(AddGroup(ownProps.selectionObjectId, [groupId], exclusionList, filters, ownProps.storeName)),
		searchUsers: (
			_ref = {},
			filters,
			searchFields,
			selectionObjectId,
			exclusionList,
			usersToExcludeSelectionId,
			excludeCurrentUser,
			signalToken
		) =>
			dispatch(
				Search(
					_ref,
					filters,
					searchFields,
					selectionObjectId,
					exclusionList,
					usersToExcludeSelectionId,
					excludeCurrentUser,
					ownProps.storeName,
					signalToken
				)
			),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(Directory);
